import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { TagLabel } from 'features/timeSeries/experiment/TagLabel'
import {
  ExperimentConfig,
  ExperimentRun,
  MethodResult,
} from 'features/timeSeries/experiment/types'
import { FC } from 'react'
import styled from 'styled-components'

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.72rem;
  th,
  td {
    text-align: left;
    padding: 0.3rem 0.35rem;
    border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
    vertical-align: top;
  }
  th {
    color: ${({ theme }) => theme.forecast.secondaryText};
    font-weight: 500;
  }
`

const MethodCell = styled.td<{ $best?: boolean }>`
  font-weight: ${({ $best }) => ($best ? 600 : 400)};
  color: ${({ theme, $best }) =>
    $best ? theme.colors.blue['400'] : theme.forecast.defaultText};
`

const Num = styled.td`
  font-variant-numeric: tabular-nums;
`

const Diff = styled.span`
  color: #f97316;
`

const Meta = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const Actions = styled.div`
  display: flex;
  gap: 0.3rem;
  flex-wrap: wrap;
`

const fmt = (value: number | null | undefined, digits = 4) =>
  value === null || value === undefined ? '—' : value.toFixed(digits)

const pct = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : `${(value * 100).toFixed(1)}%`

const configDiffSummary = (a: ExperimentConfig, b: ExperimentConfig) => {
  const diffs: string[] = []
  if (a.interval_minutes !== b.interval_minutes)
    diffs.push(
      t`interval ${{ value70_29: a.interval_minutes }}→${{ value70_51: b.interval_minutes }}`
    )
  if (a.residual_band_k !== b.residual_band_k)
    diffs.push(
      t`band_k ${{ value72_27: a.residual_band_k }}→${{ value72_48: b.residual_band_k }}`
    )
  if (a.band_estimator !== b.band_estimator)
    diffs.push(
      t`band ${{ value74_25: a.band_estimator }}→${{ value74_45: b.band_estimator }}`
    )
  if (a.knn_k !== b.knn_k)
    diffs.push(t`knn_k ${{ value75_49: a.knn_k }}→${{ value75_60: b.knn_k }}`)
  if (a.knn_fit_cap !== b.knn_fit_cap)
    diffs.push(
      t`knn_cap ${{ value77_28: a.knn_fit_cap }}→${{ value77_45: b.knn_fit_cap }}`
    )
  if (a.score_complete_rows_only !== b.score_complete_rows_only)
    diffs.push(
      t`complete_rows ${{ value80_25: a.score_complete_rows_only ? 'on' : 'off' }}→${{ value80_70: b.score_complete_rows_only ? 'on' : 'off' }}`
    )
  if (
    JSON.stringify(a.feature.driver_lags) !==
    JSON.stringify(b.feature.driver_lags)
  )
    diffs.push('lags')
  if (a.feature.include_self_history !== b.feature.include_self_history)
    diffs.push('self_history')
  if (a.split.unit !== b.split.unit) diffs.push(`split ${a.split.unit}`)
  else if (
    a.split.unit === 'custom' &&
    JSON.stringify(a.split.custom_ranges) !==
      JSON.stringify(b.split.custom_ranges)
  )
    diffs.push(t`split ranges`)
  if (JSON.stringify(a.phase_shifts) !== JSON.stringify(b.phase_shifts))
    diffs.push('phase_shifts')
  if (a.aggregation_op !== b.aggregation_op)
    diffs.push(
      t`agg ${{ value99_24: a.aggregation_op }}→${{ value99_44: b.aggregation_op }}`
    )
  return diffs.length ? diffs.join(', ') : t`same knobs`
}

/** Method keys ordered as stored, with the lowest test MAE flagged. */
const methodRows = (
  run: ExperimentRun
): Array<{ key: string; method: MethodResult; best: boolean }> => {
  const entries = Object.entries(run.result.methods ?? {})
  let bestKey: string | null = null
  let bestMae: number | null = null
  for (const [key, method] of entries) {
    const mae = method.metrics_by_role?.test?.mae
    if (
      mae !== null &&
      mae !== undefined &&
      (bestMae === null || mae < bestMae)
    ) {
      bestMae = mae
      bestKey = key
    }
  }
  return entries.map(([key, method]) => ({
    key,
    method,
    best: key === bestKey,
  }))
}

const exportRun = (run: ExperimentRun) => {
  const blob = new Blob(
    [JSON.stringify({ config: run.config, result: run.result }, null, 2)],
    { type: 'application/json' }
  )
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `sentinel-experiment-${run.id}.json`
  a.click()
  URL.revokeObjectURL(url)
}

export interface RunComparisonProps {
  runs: ExperimentRun[]
  onReload: (config: ExperimentConfig) => void
  onClear: () => void
}

/**
 * Session runs compared per method: one row per run × method with the test
 * MAE / RMSE / band coverage that already live in
 * `methods.*.metrics_by_role.test`, instead of collapsing each run to its
 * best method. The lowest-MAE method per run is highlighted; the
 * diff-vs-first-run summary and reload/export affordances span each run's
 * method rows.
 */
export const RunComparison: FC<RunComparisonProps> = ({
  runs,
  onReload,
  onClear,
}) => {
  if (!runs.length) {
    return (
      <Meta>
        <Trans>
          Train runs appear here for side-by-side comparison and persist with
          the saved experiment.
        </Trans>
      </Meta>
    )
  }
  const baseline = runs[0]
  return (
    <Wrap>
      <Actions>
        <Button variant='outline' onClick={onClear}>
          <Trans>Clear runs</Trans>
        </Button>
      </Actions>
      <Table>
        <thead>
          <tr>
            <th>
              <Trans>Run</Trans>
            </th>
            <th>
              <Trans>Method</Trans>
            </th>
            <th>
              <Trans>Test MAE</Trans>
            </th>
            <th>
              <Trans>Test RMSE</Trans>
            </th>
            <th>
              <Trans>Band cov.</Trans>
            </th>
            <th>
              <Trans>vs first</Trans>
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          {runs.map((run, runIndex) => {
            const rows = methodRows(run)
            const span = Math.max(1, rows.length)
            const runCell = (
              <td rowSpan={span}>
                {run.label}
                <div style={{ opacity: 0.7 }}>
                  {new Date(run.createdAt).toLocaleTimeString()}
                </div>
                <TagLabel tagId={run.config.target} />
              </td>
            )
            const diffCell = (
              <td rowSpan={span}>
                {runIndex === 0 ? (
                  'baseline'
                ) : (
                  <Diff>{configDiffSummary(baseline.config, run.config)}</Diff>
                )}
              </td>
            )
            const actionsCell = (
              <td rowSpan={span}>
                <Actions>
                  <Button
                    variant='outline'
                    onClick={() => onReload(run.config)}>
                    <Trans>Reload</Trans>
                  </Button>
                  <Button variant='outline' onClick={() => exportRun(run)}>
                    <Trans>Export</Trans>
                  </Button>
                </Actions>
              </td>
            )
            if (!rows.length) {
              return (
                <tr key={run.id}>
                  {runCell}
                  <td>—</td>
                  <Num>—</Num>
                  <Num>—</Num>
                  <Num>—</Num>
                  {diffCell}
                  {actionsCell}
                </tr>
              )
            }
            return rows.map(({ key, method, best }, methodIndex) => {
              const test = method.metrics_by_role?.test
              return (
                <tr key={`${run.id}-${key}`}>
                  {methodIndex === 0 && runCell}
                  <MethodCell $best={best} title={method.description || key}>
                    {method.label || key.replace('target_', '')}
                  </MethodCell>
                  <Num>{fmt(test?.mae)}</Num>
                  <Num>{fmt(test?.rmse)}</Num>
                  <Num>{pct(test?.band_coverage)}</Num>
                  {methodIndex === 0 && diffCell}
                  {methodIndex === 0 && actionsCell}
                </tr>
              )
            })
          })}
        </tbody>
      </Table>
      <Meta>
        <Trans>
          Bold method = lowest test MAE within its run. Metrics come from each
          method&apos;s held-out test split.
        </Trans>
      </Meta>
    </Wrap>
  )
}
